import { MenuItem } from "@blueprintjs/core"
import { ItemPredicate, ItemRenderer, Suggest } from "@blueprintjs/select"
import { AxiosError } from "axios"
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react"

import { FlexContainer } from "../../../../styled/flexbox.styled"
import { ToastError } from "../../../components"
import { useDebounce } from "../../../hooks/useDebounce"
import { ItemsContainer, Title } from "./TickerSuggest.styled"

interface SearchableItem {
  id: number
  name: string
  main_instrument_ticker?: string | null
}

interface ITickerSuggestProps<T extends SearchableItem> {
  setSelectedItem: Dispatch<SetStateAction<T | undefined>>
  useItems: (params: { query: string }) => {
    items: T[]
    isLoading: boolean
    error: AxiosError | null
  }
  errorMessage?: string
  selectedItem?: T
  autoFocus?: boolean
  disableTooltip?: boolean
  borderColor?: string
  disabled?: boolean
}

/**
 * Suggest component that allows searching and selecting tickers
 */

const TickerSuggest = <T extends SearchableItem>({
  useItems,
  setSelectedItem,
  errorMessage = "Unable to search items, please try to refresh the page.",
  selectedItem,
  autoFocus = true,
  disableTooltip = false,
  borderColor,
  disabled,
}: ITickerSuggestProps<T>) => {
  const [query, setQuery] = useState<string>("")

  const inputRef = useRef<HTMLInputElement | null>(null)
  // Modify the useEffect to respect autoFocus prop
  useEffect(() => {
    if (autoFocus) {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus()
        }
      }, 0)
    }
  }, [autoFocus])

  // Create a debounced function to update the query state
  const debouncedSetQuery = useDebounce((newQuery: string) => {
    setQuery(newQuery)
  }, 500) // Debounce delay in ms

  const { items, isLoading, error } = useItems({ query })

  /**
   * generate Menu to render in the dropdown
   */
  const renderTickers: ItemRenderer<T> = (item, { handleClick, modifiers }) => {
    if (!modifiers.matchesPredicate) {
      return null
    }
    return (
      <MenuItem
        active={modifiers.active}
        key={item.id}
        onClick={handleClick}
        text={
          <FlexContainer flexDirection="column">
            <Title>{item.name}</Title>
            {/* Show main ticker if available */}
            {item.main_instrument_ticker && (
              <ItemsContainer flexDirection="column">
                <span>{item.main_instrument_ticker}</span>
              </ItemsContainer>
            )}
          </FlexContainer>
        }
      />
    )
  }

  /**
   * Filter the ticker based on the query
   * Note: Server already filters by ticker, this just ensures minimum query length
   */
  const filterTickers: ItemPredicate<T> = (query, item, _index, exactMatch) => {
    // Show "No ticker found" if query is less than 2 characters
    if (query.length < 2) {
      return false
    }

    // Server already filters by ticker (including secondary listings),
    // so trust server results without re-filtering client-side
    return true
  }

  return (
    <>
      <Suggest
        items={items || []}
        itemRenderer={renderTickers}
        itemPredicate={filterTickers}
        onItemSelect={item => setSelectedItem(item)}
        noResults={
          <MenuItem
            disabled
            text={
              query.length < 2
                ? "Start typing to search..."
                : isLoading
                  ? "Loading..."
                  : "No tickers found"
            }
          />
        }
        inputValueRenderer={item => (selectedItem ? selectedItem.name : "")} // Show selected item name
        selectedItem={selectedItem} // Add selected item
        inputProps={{
          placeholder: "Search by ticker...",
          large: true,
          inputRef: inputRef,
          style: borderColor
            ? {
                border: `1px solid ${borderColor}`,
                boxShadow: "none",
              }
            : undefined,
        }}
        query={query}
        onQueryChange={debouncedSetQuery} // Update query on input change
        resetOnSelect
        resetOnClose
        fill
        popoverProps={{ disabled: disableTooltip }}
        disabled={disabled || !!error}
      />
      {error && <ToastError error={error} errorMessage={errorMessage} />}
    </>
  )
}

export default TickerSuggest
